/**
 * Servicio para manejo de datos del sistema de recomendaciones
 */

import {
  apiV1,
  loadResourceViaV1,
  DEFAULT_LIST_PAGE_SIZE,
  SEARCH_LIST_LIMIT
} from '../api/apiClient';
import {
  loadData,
  saveData,
  combineRecommendations,
  removeWatchedFromRecommendations
} from '../utils/dataUtils';
import {
  updateContentRatings,
  sortRecommendations,
  normalizeRatingsShape,
  logRatingsNormalized
} from '../utils/ratingUtils';
import { apiUrl } from '../utils/apiUrl';
import { t } from '../utils/i18n';

class DataService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutos
    this.loggingEnabled = true;
    this.watchedIdSets = new Map();
    this._generateInFlight = null;
  }

  static LIST_PAGE_SIZE = DEFAULT_LIST_PAGE_SIZE;

  _mapStatusFilterToApi(statusFilter) {
    if (statusFilter === 'omitted') return 'omitted';
    if (statusFilter === 'active' || statusFilter === 'favorites_active') return 'active';
    if (statusFilter === 'all' || statusFilter === 'favorites' || statusFilter === 'favorites_omitted') {
      return 'all';
    }
    return 'active';
  }

  /**
   * @returns {Promise<{ items, total, limit, offset, page, totalPages }>}
   */
  async getWatchedPage(contentType, page = 1, { limit = DEFAULT_LIST_PAGE_SIZE, forSearch = false } = {}) {
    const pageLimit = forSearch ? SEARCH_LIST_LIMIT : limit;
    const offset = forSearch ? 0 : (page - 1) * pageLimit;
    const result = await apiV1.listWatched(contentType, {
      limit: pageLimit,
      offset,
      sort: 'updated_at'
    });
    const totalPages = Math.max(1, Math.ceil(result.total / pageLimit));
    return { ...result, page: forSearch ? 1 : page, totalPages, pageSize: pageLimit };
  }

  /**
   * @returns {Promise<{ items, total, limit, offset, page, totalPages }>}
   */
  async getRecommendationsPage(contentType, page = 1, { limit = DEFAULT_LIST_PAGE_SIZE, statusFilter = 'active', sort = 'default' } = {}) {
    const apiStatus = this._mapStatusFilterToApi(statusFilter);
    const offset = (page - 1) * limit;
    const result = await apiV1.listRecommendations(contentType, {
      limit,
      offset,
      status: apiStatus,
      sort
    });
    const items = result.items.map((item) => {
      const { item: normalized } = normalizeRatingsShape(
        { ...item, type: contentType, category: contentType },
        { keepRootUserRating: false }
      );
      return normalized;
    });
    const totalPages = Math.max(1, Math.ceil(result.total / limit));
    return { ...result, items, page, totalPages, pageSize: limit };
  }

  /** Set of watched/listened ids for quick lookup (e.g. MoviesList). */
  async getWatchedIdSet(contentType) {
    const cacheKey = `ids_${contentType}`;
    const cached = this.watchedIdSets.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.set;
    }
    const { items } = await apiV1.listWatched(contentType, { limit: SEARCH_LIST_LIMIT, offset: 0 });
    const set = new Set(items.map((i) => String(i.id)));
    this.watchedIdSets.set(cacheKey, { set, timestamp: Date.now() });
    return set;
  }

  invalidateWatchedIdSet(contentType) {
    this.watchedIdSets.delete(`ids_${contentType}`);
  }

  async persistRecommendationChanges(contentType, { newItems = [], backfillPatches = [] } = {}) {
    const filename = {
      movie: 'movie_recommendations.json',
      series: 'series_recommendations.json',
      music: 'music_recommendations.json'
    }[contentType];
    if (!filename) return false;

    try {
      if (newItems.length > 0) {
        await apiV1.upsertRecommendationsBatch(contentType, newItems);
      }
      for (const patch of backfillPatches) {
        if (patch?.id == null) continue;
        await apiV1.patchRecommendation(contentType, patch.id, patch);
      }
      this.cache.delete(filename);
      return true;
    } catch (batchError) {
      this.log('Batch v1 falló, upsert item a item', { error: batchError.message }, 'warn');
      try {
        for (const item of newItems) {
          await apiV1.upsertRecommendation(contentType, item);
        }
        for (const patch of backfillPatches) {
          if (patch?.id == null) continue;
          await apiV1.patchRecommendation(contentType, patch.id, patch);
        }
        this.cache.delete(filename);
        return true;
      } catch (itemError) {
        this.log('Error persistiendo recomendaciones v1', { error: itemError.message }, 'error');
        return false;
      }
    }
  }

  /**
   * Log detallado de operaciones
   * @param {string} operation - Operación realizada
   * @param {Object} data - Datos relacionados
   * @param {string} level - Nivel de log (info, warn, error)
   */
  log(operation, data = {}, level = 'info') {
    if (!this.loggingEnabled) return;
    
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      operation,
      level,
      data: typeof data === 'object' ? JSON.stringify(data, null, 2) : data
    };
    
    console.log(`[${timestamp}] ${level.toUpperCase()}: ${operation}`, data);
  }

  /**
   * Obtiene datos con caché
   * @param {string} filename - Nombre del archivo
   * @returns {Promise<Object>} Datos cargados
   */
  async getCachedData(filename) {
    const cacheKey = filename;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    
    let data = null;
    try {
      data = await loadResourceViaV1(filename);
    } catch (v1Error) {
      this.log('API v1 no disponible, usando load-data legacy', {
        filename,
        error: v1Error.message
      }, 'warn');
      data = await loadData(filename);
    }

    if (data != null) {
      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now()
      });
    }

    return data;
  }

  /**
   * Limpia el caché
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Limpia todos los datos de localStorage
   */
  clearLocalStorage() {
    localStorage.removeItem('user_preferences');
    localStorage.removeItem('watched_movie');
    localStorage.removeItem('watched_series');
    localStorage.removeItem('watched_music');
  }

  /**
   * Exporta todos los datos del usuario actual a un archivo JSON (desde la API)
   */
  async exportData() {
    const [preferences, movies, series, music] = await Promise.all([
      this.getUserPreferences(),
      this.getWatchedMovies(),
      this.getWatchedSeries(),
      this.getListenedMusic()
    ]);
    const data = {
      user_preferences: preferences,
      watched_movies: movies,
      watched_series: series,
      watched_music: music
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'recommendations_data.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Descarga un archivo JSON específico
   * @param {string} filename - Nombre del archivo
   */
  async downloadSpecificFile(filename) {
    try {
      let data = null;
      
      switch (filename) {
        case 'user_preferences.json':
          data = await this.getUserPreferences();
          break;
        case 'movies_watched.json':
          const movies = await this.getWatchedMovies();
          data = { movies };
          break;
        case 'series_watched.json':
          const series = await this.getWatchedSeries();
          data = { series };
          break;
        case 'music_listened.json':
          const music = await this.getListenedMusic();
          data = { music };
          break;
        case 'movie_recommendations.json':
          const movieRecs = await this.getMovieRecommendations();
          data = { recommendations: movieRecs };
          break;
        case 'series_recommendations.json':
          const seriesRecs = await this.getSeriesRecommendations();
          data = { recommendations: seriesRecs };
          break;
        case 'music_recommendations.json':
          const musicRecs = await this.getMusicRecommendations();
          data = { recommendations: musicRecs };
          break;
        default:
          throw new Error(`Archivo ${filename} no reconocido`);
      }
      
      if (data) {
        downloadJSONFile(filename, data);
        return true;
      }
      return false;
    } catch (error) {
      console.error(`Error descargando ${filename}:`, error);
      return false;
    }
  }

  /**
   * Obtiene información de los recursos de datos (desde BD vía API).
   */
  async getFileInfo() {
    try {
      const res = await fetch(apiUrl('/api/file-info'), { credentials: 'include', cache: 'no-store' });
      if (res.ok) return await res.json();
      return [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Muestra en consola los datos del recurso (desde BD).
   */
  async showFileContent(filename) {
    await showFileContent(filename);
  }

  /**
   * Actualiza el contenido visto/escuchado
   * @param {string} type - Tipo de contenido (movie, series, music)
   * @param {string} contentId - ID del contenido
   * @param {Object} updates - Datos a actualizar
   * @returns {Promise<boolean>} True si se actualizó correctamente
   */
  async updateWatchedContent(type, contentId, updates) {
    try {
      const filename = { movie: 'movies_watched.json', series: 'series_watched.json', music: 'music_listened.json' }[type];
      if (!filename) throw new Error('Tipo de contenido no válido');

      const existing = await apiV1.getWatched(type, contentId);
      if (!existing) throw new Error('Contenido no encontrado');

      await apiV1.patchWatched(type, contentId, updates);
      this.cache.delete(filename);
      return true;
    } catch (error) {
      console.error('Error actualizando contenido:', error);
      return false;
    }
  }

  /**
   * Elimina contenido del historial visto/escuchado y lo devuelve a recomendaciones
   * @param {string} type - Tipo de contenido (movie, series, music)
   * @param {string} contentId - ID del contenido
   * @returns {Promise<boolean>} True si se eliminó correctamente
   */
  async removeWatchedContent(type, contentId) {
    try {
      console.log('removeWatchedContent llamado con:', { type, contentId });
      let watchedFilename, watchedArrayKey, watchedLocalKey;
      let recommendationsFilename, recommendationsArrayKey;
      
      switch (type) {
        case 'movie':
          watchedFilename = 'movies_watched.json';
          watchedArrayKey = 'movies';
          watchedLocalKey = 'watched_movie';
          recommendationsFilename = 'movie_recommendations.json';
          recommendationsArrayKey = 'recommendations';
          break;
        case 'series':
          watchedFilename = 'series_watched.json';
          watchedArrayKey = 'series';
          watchedLocalKey = 'watched_series';
          recommendationsFilename = 'series_recommendations.json';
          recommendationsArrayKey = 'recommendations';
          break;
        case 'music':
          watchedFilename = 'music_listened.json';
          watchedArrayKey = 'music';
          watchedLocalKey = 'watched_music';
          recommendationsFilename = 'music_recommendations.json';
          recommendationsArrayKey = 'recommendations';
          break;
        default:
          throw new Error('Tipo de contenido no válido');
      }

      let contentToRemove = null;
      try {
        contentToRemove = await apiV1.getWatched(type, contentId);
      } catch (_) {
        const watchedData = await this.getCachedData(watchedFilename);
        const watchedArray = watchedData?.[watchedArrayKey] || [];
        contentToRemove = watchedArray.find(item => item.id === contentId);
      }
      if (!contentToRemove) {
        throw new Error('Contenido no encontrado en el historial');
      }
      
      console.log('Contenido encontrado para eliminar:', contentToRemove);
      console.log('Calificación del usuario:', contentToRemove.user_rating);

      await apiV1.deleteWatched(type, contentId);
      this.invalidateWatchedIdSet(type);

      let existsInRecommendations = false;
      try {
        await apiV1.getRecommendation(type, contentId);
        existsInRecommendations = true;
      } catch (_) {
        const recommendationsData = await this.getCachedData(recommendationsFilename);
        const recommendationsArray = recommendationsData?.[recommendationsArrayKey] || [];
        existsInRecommendations = recommendationsArray.some(item => item.id === contentId);
      }
      
      if (!existsInRecommendations) {
        // Preparar el contenido para volver a recomendaciones
        const contentForRecommendations = {
          ...contentToRemove,
          // Remover campos específicos del historial
          watched_date: undefined,
          listened_date: undefined,
          notes: undefined,
          // Mantener campos de recomendación y calificación del usuario
          type: type,
          user_rating: contentToRemove.user_rating, // Mantener la calificación del usuario
          ratings: {
            user_rating: contentToRemove.user_rating, // Incluir en ratings también
            external_rating: contentToRemove.ratings?.external_rating || 0,
            ai_rating: contentToRemove.ratings?.ai_rating || 0,
            average_rating: contentToRemove.ratings?.average_rating || 0
          }
        };
        
        console.log('Contenido preparado para recomendaciones:', contentForRecommendations);
        console.log('Calificación preservada:', contentForRecommendations.user_rating);

        await apiV1.upsertRecommendation(type, contentForRecommendations);
      }

      // Limpiar caché
      this.cache.delete(watchedFilename);
      this.cache.delete(recommendationsFilename);

      this.log('Contenido devuelto a recomendaciones (sin generar nuevas con IA)', { type, contentId });
      await this.indexRecommendationsInRagIfEnabled(type);

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('recommendationsUpdated', { detail: { contentType: type } }));
      }

      console.log(`✅ Contenido ${contentId} eliminado del historial y devuelto a recomendaciones`);
      return true;
    } catch (error) {
      console.error('Error eliminando contenido:', error);
      return false;
    }
  }

  /**
   * Obtiene las preferencias del usuario
   * @returns {Promise<Object>} Preferencias del usuario
   */
  async getUserPreferences() {
    try {
      // Siempre cargar desde la API (datos por usuario/sesión); no usar localStorage para multi-usuario
      return await this.getCachedData('user_preferences.json');
    } catch (error) {
      console.error('Error obteniendo preferencias:', error);
      // Fallback a datos por defecto
      return {
        favorite_actors: [],
        favorite_genres: [],
        preferred_movie_types: [],
        rating_threshold: 7,
        max_recommendations: 20,
        language_preference: "español",
        content_rating: "PG-13",
        sorting_preferences: {
          default_sort: "average_rating",
          available_sorts: ["user_rating", "external_rating", "ai_rating", "average_rating"],
          weight_preferences: {
            user_rating_weight: 0.4,
            external_rating_weight: 0.3,
            ai_rating_weight: 0.3
          }
        }
      };
    }
  }

  /**
   * Actualiza las preferencias del usuario
   * @param {Object} preferences - Nuevas preferencias
   * @param {boolean} showContent - Si debe mostrar el contenido en consola
   * @returns {Promise<boolean>} True si se actualizó correctamente
   */
  async updateUserPreferences(preferences, showContent = false) {
    try {
      await apiV1.patchPreferences(preferences);
      this.cache.delete('user_preferences.json');
      return true;
    } catch (error) {
      this.log('API v1 preferences falló, legacy save-data', { error: error.message }, 'warn');
      try {
        const success = await saveData('user_preferences.json', preferences);
        this.cache.delete('user_preferences.json');
        return success;
      } catch (legacyErr) {
        logError('Error actualizando preferencias:', legacyErr);
        return false;
      }
    }
  }

  /**
   * Obtiene el historial de películas vistas
   * @returns {Promise<Array>} Películas vistas
   */
  async getWatchedMovies() {
    try {
      // Siempre desde la API (datos por usuario/sesión)
      const data = await this.getCachedData('movies_watched.json');
      return data?.movies || [];
    } catch (error) {
      console.error('Error obteniendo películas vistas:', error);
      return [];
    }
  }

  /**
   * Obtiene el historial de series vistas
   * @returns {Promise<Array>} Series vistas
   */
  async getWatchedSeries() {
    try {
      // Siempre desde la API (datos por usuario/sesión)
      const data = await this.getCachedData('series_watched.json');
      return data?.series || [];
    } catch (error) {
      console.error('Error obteniendo series vistas:', error);
      return [];
    }
  }

  /**
   * Obtiene el historial de música escuchada
   * @returns {Promise<Array>} Música escuchada
   */
  async getListenedMusic() {
    try {
      // Siempre desde la API (datos por usuario/sesión)
      const data = await this.getCachedData('music_listened.json');
      return data?.music || [];
    } catch (error) {
      console.error('Error obteniendo música escuchada:', error);
      return [];
    }
  }

  /**
   * Obtiene todas las recomendaciones
   * @returns {Promise<Array>} Recomendaciones combinadas
   */
  async getAllRecommendations() {
    const [movieRecs, seriesRecs, musicRecs] = await Promise.all([
      this.getMovieRecommendations(),
      this.getSeriesRecommendations(),
      this.getMusicRecommendations()
    ]);

    const combined = combineRecommendations(movieRecs, seriesRecs, musicRecs);
    const needsBackfill = combined.some(r => !r.recommended_at && !r.recommendedAt);
    if (needsBackfill && !this._recommendedAtBackfillDone) {
      this._recommendedAtBackfillDone = true;
      const saved = await this.backfillRecommendedAt();
      if (saved) {
        this.cache.delete('movie_recommendations.json');
        this.cache.delete('series_recommendations.json');
        this.cache.delete('music_recommendations.json');
        [movieRecs, seriesRecs, musicRecs] = await Promise.all([
          this.getMovieRecommendations(),
          this.getSeriesRecommendations(),
          this.getMusicRecommendations()
        ]);
        return combineRecommendations(movieRecs, seriesRecs, musicRecs);
      }
    }
    return combined;
  }

  /**
   * Rellena recommended_at en recomendaciones que no lo tienen (una vez por sesión).
   * @returns {Promise<boolean>} True si se guardó algo
   */
  async backfillRecommendedAt() {
    const nowIso = new Date().toISOString();
    const types = ['movie', 'series', 'music'];
    let anySaved = false;
    for (const contentType of types) {
      const { items } = await apiV1.listRecommendations(contentType, { status: 'all', limit: SEARCH_LIST_LIMIT });
      const patches = items
        .filter((r) => !r.recommended_at && !r.recommendedAt)
        .map((r) => ({ id: r.id, recommended_at: nowIso }));
      if (patches.length === 0) continue;
      const ok = await this.persistRecommendationChanges(contentType, { backfillPatches: patches });
      if (ok) anySaved = true;
    }
    return anySaved;
  }

  /**
   * Obtiene recomendaciones de películas
   * @returns {Promise<Array>} Recomendaciones de películas
   */
  async getMovieRecommendations() {
    const data = await this.getCachedData('movie_recommendations.json');
    return data?.recommendations || [];
  }

  /**
   * Obtiene recomendaciones de series
   * @returns {Promise<Array>} Recomendaciones de series
   */
  async getSeriesRecommendations() {
    const data = await this.getCachedData('series_recommendations.json');
    return data?.recommendations || [];
  }

  /**
   * Obtiene recomendaciones de música
   * @returns {Promise<Array>} Recomendaciones de música
   */
  async getMusicRecommendations() {
    const data = await this.getCachedData('music_recommendations.json');
    return data?.recommendations || [];
  }

  /**
   * Obtiene recomendaciones filtradas y ordenadas
   * @param {string} type - Tipo de contenido (movie, series, music, all)
   * @param {string} sortBy - Criterio de ordenamiento
   * @param {string} searchQuery - Término de búsqueda
   * @returns {Promise<Array>} Recomendaciones filtradas y ordenadas
   */
  async getFilteredRecommendations(type = 'all', sortBy = 'average_rating', searchQuery = '') {
    let recommendations = [];

    if (type === 'all') {
      recommendations = await this.getAllRecommendations();
    } else {
      const typeMap = {
        movie: 'getMovieRecommendations',
        series: 'getSeriesRecommendations',
        music: 'getMusicRecommendations'
      };
      
      if (typeMap[type]) {
        recommendations = await this[typeMap[type]]();
      }
    }

    // Filtrar por búsqueda
    if (searchQuery) {
      recommendations = recommendations.filter(rec => 
        rec.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (rec.artist && rec.artist.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Ordenar
    return sortRecommendations(recommendations, sortBy);
  }

  /**
   * Marca contenido como visto
   * @param {string|Object} typeOrContent - Tipo de contenido o contenido completo
   * @param {Object|number} contentOrRating - Contenido o calificación
   * @returns {Promise<boolean>} True si se marcó correctamente
   */
  async markAsWatched(typeOrContent, contentOrRating) {
    try {
      let content, userRating, contentType;
      
      // Determinar si el primer parámetro es un tipo o contenido
      if (typeof typeOrContent === 'string') {
        // Nuevo formato: markAsWatched('movie', content)
        contentType = typeOrContent;
        content = contentOrRating;
        userRating = content.user_rating || content.rating;
      } else {
        // Formato anterior: markAsWatched(content, rating)
        content = typeOrContent;
        userRating = contentOrRating;
        contentType = content.type || 'movie';
      }

      const preferences = await this.getUserPreferences();
      const nowIso = new Date().toISOString();
      const updatedContent = {
        ...content,
        ratings: {
          ...content.ratings,
          user_rating: userRating
        }
      };

      // Actualizar calificaciones
      const contentWithRatings = updateContentRatings(updatedContent, preferences);

      // Asegurar que el objeto guardado tenga user_rating y fecha+hora en la raíz (para el historial)
      const toSave = {
        ...contentWithRatings,
        user_rating: contentWithRatings.user_rating ?? contentWithRatings.ratings?.user_rating ?? userRating,
        ...(contentType === 'music'
          ? { listened_date: contentWithRatings.listened_date || nowIso }
          : { watched_date: contentWithRatings.watched_date || contentWithRatings.date_watched || nowIso })
      };

      const watchedFilenameByType = {
        movie: 'movies_watched.json',
        series: 'series_watched.json',
        music: 'music_listened.json'
      };
      const filename = watchedFilenameByType[contentType];
      if (!filename) throw new Error('Tipo de contenido no válido');

      // Guardar solo este ítem en la API (evita 413 cuando el historial es muy grande)
      await apiV1.upsertWatched(contentType, toSave);
      this.cache.delete(filename);
      this.invalidateWatchedIdSet(contentType);
      
      await this.updateRecommendationsAfterWatching(content);

      // Rellenar si quedan pocas activas: Groq si hay cuota; si no, pool de comunidad (servidor)
      await this.updateRecommendationsAfterChange(contentType, 'add', toSave);

      return true;
    } catch (error) {
      console.error('Error marking content as watched:', error);
      return false;
    }
  }

  /**
   * Actualiza las recomendaciones después de marcar contenido como visto
   * @param {Object} watchedContent - Contenido marcado como visto
   * @returns {Promise<boolean>} True si se actualizó correctamente
   */
  async updateRecommendationsAfterWatching(watchedContent) {
    try {
      const typeMap = {
        movie: 'movie_recommendations.json',
        series: 'series_recommendations.json',
        music: 'music_recommendations.json'
      };

      const filename = typeMap[watchedContent.type];
      if (!filename) return false;

      const currentData = await this.getCachedData(filename);
      const currentRecommendations = currentData?.recommendations || [];

      // Función para normalizar títulos
      const normalizeTitle = (title) => {
        return title.toLowerCase()
          .replace(/[^\w\s]/g, '') // Remover caracteres especiales
          .replace(/\s+/g, ' ')    // Normalizar espacios
          .trim();
      };

      const watchedTitle = normalizeTitle(watchedContent.title);

      // Remover el contenido visto de las recomendaciones (por ID y por título)
      let removedCount = 0;
      let success = false;

      // Eliminar por id (payload pequeño)
      if (watchedContent.id != null) {
        try {
          await apiV1.deleteRecommendation(watchedContent.type || 'movie', watchedContent.id);
          removedCount += 1;
        } catch (_) { /* not in list */ }
      }

      // Si no había id o quedó duplicado por título, eliminar coincidencias por título vía save parcial
      const remainingByTitle = currentRecommendations.filter(rec => {
        if (watchedContent.id != null && rec.id === watchedContent.id) return false;
        const recTitle = normalizeTitle(rec.title);
        return recTitle === watchedTitle;
      });

      for (const rec of remainingByTitle) {
        if (rec.id != null) {
          try {
            await apiV1.deleteRecommendation(watchedContent.type || 'movie', rec.id);
            removedCount += 1;
          } catch (_) { /* skip */ }
        }
      }

      success = true;

      this.log('Actualización de recomendaciones después de marcar como visto', {
        contentType: watchedContent.type,
        watchedTitle: watchedContent.title,
        originalCount: currentRecommendations.length,
        removedCount
      });

      if (success) {
        this.cache.delete(filename);
        this.log('Recomendaciones actualizadas exitosamente', {
          filename,
          removedCount,
          finalCount: currentRecommendations.length - removedCount
        });
      }

      return success;
    } catch (error) {
      logError('Error updating recommendations:', error);
      return false;
    }
  }

  /**
   * Cambia el estado de favorito de una recomendación
   * @param {string} contentType - Tipo de contenido (movie, series, music)
   * @param {string} recommendationId - ID de la recomendación
   * @param {boolean} isFavorite - Nuevo estado de favorito
   * @returns {Promise<boolean>} True si se actualizó correctamente
   */
  async setRecommendationFavorite(contentType, recommendationId, isFavorite) {
    try {
      const filenameMap = {
        movie: 'movie_recommendations.json',
        series: 'series_recommendations.json',
        music: 'music_recommendations.json'
      };
      const filename = filenameMap[contentType];
      if (!filename) return false;

      await apiV1.patchRecommendation(contentType, recommendationId, {
        is_favorite: isFavorite,
        favorite: isFavorite
      });
      this.cache.delete(filename);
      const success = true;

      if (success) {
        this.log('Estado de favorito actualizado', { contentType, recommendationId, isFavorite });
        window.dispatchEvent(new CustomEvent('recommendationsUpdated', { detail: { contentType } }));
      }
      
      return success;
    } catch (error) {
      this.log('Error actualizando favorito de recomendación', { error: error.message }, 'error');
      return false;
    }
  }

  /**
   * Cambia el estado de una recomendación (active/omitted)
   * @param {string} contentType - Tipo de contenido (movie, series, music)
   * @param {string} recommendationId - ID de la recomendación
   * @param {string} status - Nuevo estado ('active' o 'omitted')
   * @returns {Promise<boolean>} True si se actualizó correctamente
   */
  async setRecommendationStatus(contentType, recommendationId, status) {
    try {
      const filenameMap = {
        movie: 'movie_recommendations.json',
        series: 'series_recommendations.json',
        music: 'music_recommendations.json'
      };
      const filename = filenameMap[contentType];
      if (!filename) return false;

      await apiV1.patchRecommendation(contentType, recommendationId, { status });
      this.cache.delete(filename);
      this.log('Estado de recomendación actualizado', { contentType, recommendationId, status });
      window.dispatchEvent(new CustomEvent('recommendationsUpdated', { detail: { contentType } }));
      return true;
    } catch (error) {
      this.log('Error actualizando estado de recomendación', { error: error.message }, 'error');
      return false;
    }
  }

  /**
   * Genera recomendaciones vía API v1 en el servidor (Phase 2).
   * @param {string} contentType - movie | series | music
   * @param {number} count
   * @param {Object} options - { force, recentlyRated, useCrossContentContext, language }
   */
  async generateAutoRecommendations(contentType = 'movie', count = 20, options = {}) {
    if (this._generateInFlight) {
      this.log('Generación ya en curso; reutilizando la misma petición');
      this.emitLoadingEvent(true, t('recommendations.generationInProgress', options.language || 'es'));
      return this._generateInFlight;
    }
    this._generateInFlight = this._runGenerateAutoRecommendations(contentType, count, options).finally(
      () => {
        this._generateInFlight = null;
      }
    );
    return this._generateInFlight;
  }

  async _runGenerateAutoRecommendations(contentType = 'movie', count = 20, options = {}) {
    const filenameMap = {
      movie: 'movie_recommendations.json',
      series: 'series_recommendations.json',
      music: 'music_recommendations.json'
    };
    const filename = filenameMap[contentType];

    try {
      this.log('Generación en servidor (API v1)', { contentType, count, force: options.force });
      this.emitLoadingEvent(true, 'Consultando con la IA...');

      const result = await apiV1.generateRecommendations({
        contentType,
        count,
        force: options.force === true,
        recentlyRatedId: options.recentlyRated?.id,
        useCrossContentContext: options.useCrossContentContext === true,
        language: options.language || 'es'
      });

      if (result.groqStatus) {
        console.warn('[MisKosas Groq] estado', JSON.stringify(result.groqStatus));
      }
      if (result.groqError) {
        console.warn('[MisKosas Groq] límite o pago', result.groqError, result.message);
      }

      if (result.skipped) {
        this.log('Generación omitida por umbral', result);
        console.warn('[MisKosas] generación omitida (umbral)', result.message || result.reason);
        this.emitLoadingEvent(false, result.message || '');
        return true;
      }

      if (!result.success) {
        console.warn('[MisKosas] generación fallida', result.warning, result.message, result);
        this.emitLoadingEvent(false, result.message || 'Error al generar', true);
        if (filename) this.cache.delete(filename);
        window.dispatchEvent(new CustomEvent('recommendationsUpdated', { detail: { contentType } }));
        return false;
      }

      if (result.source === 'community') {
        console.warn('[MisKosas] recomendaciones desde comunidad', result.generated, result.message);
      }
      if (result.source === 'alibaba') {
        console.warn('[MisKosas Alibaba] recomendaciones generadas', result.generated, result.message);
      }

      if (filename) this.cache.delete(filename);
      this.log('Generación completada', result);
      this.emitLoadingEvent(false);
      window.dispatchEvent(new CustomEvent('recommendationsUpdated', { detail: { contentType } }));
      return (result.generated ?? 0) > 0;
    } catch (error) {
      const isTimeout =
        error.code === 'CLIENT_TIMEOUT' ||
        error.message === 'GENERATE_TIMEOUT' ||
        /timeout|NETWORK_CHANGED/i.test(error.message || '');
      const lang = options.language || 'es';
      const userMessage = isTimeout
        ? t('recommendations.generationTimeout', lang)
        : error.message || 'Error';
      this.log('Error en generateAutoRecommendations', { error: error.message }, 'error');
      console.warn('[MisKosas] generación error', error.message);
      this.emitLoadingEvent(false, userMessage, true);
      return false;
    }
  }

  /**
   * Construye la query de texto para RAG (recuperación por similitud).
   * @param {string} contentType - movie | series | music
   * @param {Object} preferences - Preferencias del usuario
   * @returns {string}
   */
  buildRagQuery(contentType, preferences = {}) {
    const typeLabel = contentType === 'movie' ? 'películas' : contentType === 'series' ? 'series' : 'música';
    const genres = (preferences.favorite_genres || []).slice(0, 5).join(' ');
    const actors = (preferences.favorite_actors || []).slice(0, 3).join(' ');
    const parts = [typeLabel, 'bien valoradas'];
    if (genres) parts.unshift(genres);
    if (actors && contentType !== 'music') parts.push(actors);
    return parts.join(' ').trim() || `${typeLabel} bien valoradas`;
  }

  /**
   * Emite eventos de carga para mostrar/ocultar el modal
   * @param {boolean} isLoading - Si está cargando
   * @param {string} message - Mensaje a mostrar
   */
  emitLoadingEvent(isLoading, message = '', isError = false) {
    const event = new CustomEvent('recommendationLoading', {
      detail: { isLoading, message, isError }
    });
    window.dispatchEvent(event);
  }

  /**
   * Index recommendations in RAG when enabled (e.g. after returning an item to recommendations).
   */
  async indexRecommendationsInRagIfEnabled(contentType) {
    try {
      const configRes = await fetch(apiUrl('/api/config'), { credentials: 'include' });
      const config = configRes.ok ? await configRes.json() : {};
      if (!config.ragEnabled) return;
      await fetch(apiUrl('/api/embeddings/index-recommendations'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contentType }),
        credentials: 'include'
      });
      this.log('RAG: recommendations indexed after returning item', { contentType });
    } catch (err) {
      this.log('RAG: could not index recommendations (continuing)', { error: err.message }, 'warn');
    }
  }

  /**
   * Actualiza un archivo de recomendaciones de manera inteligente
   * @param {string} filename - Nombre del archivo
   * @param {Array} newRecommendations - Nuevas recomendaciones del LLM
   * @param {string} contentType - Tipo de contenido
   * @returns {Promise<boolean>} True si se actualizó correctamente
   */
  async updateRecommendationsFile(filename, newRecommendations, contentType) {
    try {
      // Cargar recomendaciones existentes
      const existingData = await this.getCachedData(filename);
      const existingRecommendations = existingData?.recommendations || [];
      
      // Cargar contenido ya visto para filtrarlo
      const watchedContent = await this.getWatchedContentByType(contentType);
      const watchedTitles = new Set(watchedContent.map(item => 
        item.title.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim()
      ));
      
      this.log('Actualizando recomendaciones inteligentemente', {
        contentType,
        existingCount: existingRecommendations.length,
        newCount: newRecommendations.length,
        watchedCount: watchedContent.length
      });

      // Función para normalizar títulos para comparación
      const normalizeTitle = (title) => {
        return title.toLowerCase()
          .replace(/[^\w\s]/g, '') // Remover caracteres especiales
          .replace(/\s+/g, ' ')    // Normalizar espacios
          .trim();
      };

      // Función para detectar duplicados
      const isDuplicate = (newRec, existingRec) => {
        const newTitle = normalizeTitle(newRec.title);
        const existingTitle = normalizeTitle(existingRec.title);
        
        // Comparar títulos normalizados
        if (newTitle === existingTitle) {
          return true;
        }
        
        // Comparar por año si está disponible
        if (newRec.year && existingRec.year && newRec.year === existingRec.year) {
          const similarity = this.calculateStringSimilarity(newTitle, existingTitle);
          return similarity > 0.8; // 80% de similitud
        }
        
        return false;
      };

      // Filtrar duplicados y contenido ya visto de las nuevas recomendaciones
      const uniqueNewRecommendations = newRecommendations.filter(newRec => {
        const normalizedTitle = normalizeTitle(newRec.title);
        
        // Verificar si ya está visto
        if (watchedTitles.has(normalizedTitle)) {
          this.log('Filtrando recomendación ya vista', { title: newRec.title, contentType });
          return false;
        }
        
        // Verificar si es duplicado de recomendaciones existentes
        const isDuplicateOfExisting = existingRecommendations.some(existingRec => 
          isDuplicate(newRec, existingRec)
        );
        
        if (isDuplicateOfExisting) {
          this.log('Filtrando recomendación duplicada', { title: newRec.title, contentType });
          return false;
        }
        
        return true;
      });

      // Combinar recomendaciones existentes con las nuevas únicas
      const updatedRecommendations = [...existingRecommendations, ...uniqueNewRecommendations];

      this.log('Resultado de actualización inteligente', {
        contentType,
        originalExisting: existingRecommendations.length,
        newFromLLM: newRecommendations.length,
        duplicatesRemoved: newRecommendations.length - uniqueNewRecommendations.length,
        finalCount: updatedRecommendations.length
      });

      const backfillPatches = existingWithDate
        .filter((r) => !r.recommended_at && !r.recommendedAt)
        .map((r) => ({ id: r.id, recommended_at: nowIso, status: r.status || 'active' }));

      const success = await this.persistRecommendationChanges(contentType, {
        newItems: uniqueNewWithDate,
        backfillPatches
      });

      if (success) {
        this.log('Recomendaciones actualizadas exitosamente', {
          contentType,
          filename,
          finalCount: updatedRecommendations.length,
          added: uniqueNewRecommendations.length,
          duplicatesRemoved: newRecommendations.length - uniqueNewRecommendations.length
        });
      }
      
      return success;
    } catch (error) {
      this.log('Error actualizando recomendaciones', {
        contentType,
        filename,
        error: error.message
      }, 'error');
      return false;
    }
  }

  /**
   * Obtiene contenido ya visto por tipo
   * @param {string} contentType - Tipo de contenido (movie, series, music)
   * @returns {Promise<Array>} Array de contenido ya visto
   */
  async getWatchedContentByType(contentType) {
    try {
      const typeMap = {
        movie: 'movies_watched.json',
        series: 'series_watched.json',
        music: 'music_listened.json'
      };

      const filename = typeMap[contentType];
      if (!filename) return [];

      const data = await this.getCachedData(filename);
      const arrayKey = contentType === 'movie' ? 'movies' : 
                      contentType === 'series' ? 'series' : 'music';
      
      return data?.[arrayKey] || [];
    } catch (error) {
      this.log('Error obteniendo contenido visto', { contentType, error: error.message }, 'error');
      return [];
    }
  }

  /**
   * Calcula la similitud entre dos strings
   * @param {string} str1 - Primer string
   * @param {string} str2 - Segundo string
   * @returns {number} Similitud entre 0 y 1
   */
  calculateStringSimilarity(str1, str2) {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    
    if (longer.length === 0) {
      return 1.0;
    }
    
    const distance = this.levenshteinDistance(longer, shorter);
    return (longer.length - distance) / longer.length;
  }

  /**
   * Calcula la distancia de Levenshtein entre dos strings
   * @param {string} str1 - Primer string
   * @param {string} str2 - Segundo string
   * @returns {number} Distancia de Levenshtein
   */
  levenshteinDistance(str1, str2) {
    const matrix = [];
    
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    return matrix[str2.length][str1.length];
  }

  /**
   * Actualiza recomendaciones después de cambios en el contenido
   * @param {string} contentType - Tipo de contenido afectado
   * @param {string} action - Acción realizada (add, remove, update)
   * @param {Object|null} recentContent - Si action es 'add', contenido recién calificado para contexto en el prompt
   * @returns {Promise<boolean>} True si se actualizaron correctamente
   */
  async updateRecommendationsAfterChange(contentType, action = 'add', recentContent = null) {
    try {
      this.log('Actualizando recomendaciones después de cambio', { contentType, action });
      
      const options = {
        force: false,
        ...(action === 'add' && recentContent ? { recentlyRated: recentContent } : {})
      };
      const success = await this.generateAutoRecommendations(contentType, 20, options);
      
      if (success) {
        this.log('Recomendaciones actualizadas exitosamente', { contentType, action });
      } else {
        this.log('No se pudieron actualizar las recomendaciones', { contentType, action }, 'warn');
      }
      
      return success;
    } catch (error) {
      this.log('Error actualizando recomendaciones después de cambio', {
        contentType,
        action,
        error: error.message
      }, 'error');
      return false;
    }
  }

  /**
   * Obtiene estadísticas del usuario
   * @returns {Promise<Object>} Estadísticas del usuario
   */
  /**
   * One-time repair: persist normalized ratings for legacy rows (duplicate root keys).
   * Logs each fixed item and a summary in the console.
   * @returns {Promise<number>} Number of items updated in the database
   */
  async repairLegacyRatingsNormalization() {
    console.warn('[MisKosas ratings] repair: iniciando comprobación en BD…');
    const types = ['movie', 'series', 'music'];
    let totalRepaired = 0;

    for (const contentType of types) {
      const watched = await apiV1.listWatched(contentType, { limit: SEARCH_LIST_LIMIT, offset: 0 });
      for (const raw of watched.items || []) {
        const { item, normalized, changes } = normalizeRatingsShape(raw, { keepRootUserRating: true });
        if (!normalized) continue;
        await apiV1.upsertWatched(contentType, item);
        logRatingsNormalized(item, changes, `repair:watched:${contentType}`);
        totalRepaired += 1;
      }

      const recs = await apiV1.listRecommendations(contentType, { status: 'all', limit: SEARCH_LIST_LIMIT, offset: 0 });
      for (const raw of recs.items || []) {
        const { item, normalized, changes } = normalizeRatingsShape(raw, { keepRootUserRating: false });
        if (!normalized) continue;
        await apiV1.upsertRecommendation(contentType, item);
        logRatingsNormalized(item, changes, `repair:recommendations:${contentType}`);
        totalRepaired += 1;
      }
    }

    if (totalRepaired > 0) {
      this.clearCache();
      this.log('Ratings legacy repair completed', { totalRepaired }, 'info');
      console.warn(`[MisKosas ratings] repair finished: ${totalRepaired} item(s) saved to database`);
    } else {
      console.warn('[MisKosas ratings] repair: no legacy duplicate ratings found');
    }

    return totalRepaired;
  }

  async getUserStats() {
    try {
      const [movies, series, music] = await Promise.all([
        this.getWatchedMovies(),
        this.getWatchedSeries(),
        this.getListenedMusic()
      ]);

      const userData = { movies, series, music };
      
      // Calcular estadísticas
      const totalWatched = movies.length + series.length + music.length;
      const totalRating = [...movies, ...series, ...music].reduce((sum, item) => {
        return sum + (item.ratings?.user_rating || 0);
      }, 0);
      const averageRating = totalWatched > 0 ? totalRating / totalWatched : 0;

      return {
        totalWatched,
        moviesWatched: movies.length,
        seriesWatched: series.length,
        musicListened: music.length,
        averageRating: Math.round(averageRating * 10) / 10
      };
    } catch (error) {
      console.error('Error getting user stats:', error);
      return {
        totalWatched: 0,
        moviesWatched: 0,
        seriesWatched: 0,
        musicListened: 0,
        averageRating: 0
      };
    }
  }
}

// Instancia singleton
const dataService = new DataService();
export default dataService;
